/**
 * The staff list, once, for every control that needs to name a recruiter.
 *
 * RecruiterFilter already held this query and the comment above it explains
 * why it is a component rather than three copies: the three screens that
 * narrow by owner had drifted, two asking for inactive accounts and one not,
 * so the same filter offered different names depending on the page.
 *
 * The reassignment picker needs the same list and is not a filter, so it
 * cannot reuse that component -- which would have put the query back in two
 * places, which is where this started. The query moves here instead and both
 * read it.
 *
 * One key, so the two fetch once between them and cannot disagree even while
 * loading.
 */
import { useQuery } from '@tanstack/react-query';
import { client } from '../api/client';
import type { Page, User } from '../types';

export function useRecruiterOptions(enabled = true) {
  return useQuery<Page<User>>({
    queryKey: ['users', 'recruiter-filter'],
    queryFn: async () =>
      (
        await client.get('/api/v1/admin/users', {
          // Inactive included deliberately: a recruiter who has left is still
          // the recruiter on the rows they made, and every account the
          // importer created is deactivated by design. Which of them may be
          // *offered* is the caller's question -- see groupRecruiters for the
          // filter, and reassignTargets for the narrower one.
          params: { limit: 200, includeInactive: true },
        })
      ).data,
    // The endpoint is admin-only. A screen that renders a control for some
    // roles and not others must not fire the request for the rest, or every
    // recruiter opening it collects a 403 for a list they were never shown.
    enabled,
  });
}
